import type { ReactNode } from 'react';

/**
 * Conservatively bolds the scannable facts in a body-text string.
 *
 * Targets:
 *  - dollar amounts ($X, $X,XXX, $X.XX)
 *  - numbers with units (X,XXX agents | X advisors | X years | X states | X% etc.)
 *  - 4-digit years (1900–2099)
 *  - brand phrases (LuxRally, Seller of Travel, etc.)
 *  - friction-killer phrases (free training, no fees, no commitment, 80/20)
 *
 * Overlapping matches are merged so a span like "$66,400 in 2024" gets a single
 * <strong> wrapper covering both numbers if they're adjacent.
 *
 * Use cases: AI-generated state paragraphs and FAQ answers where we want to
 * lift facts without regenerating content.
 */

const PATTERNS: RegExp[] = [
    // Dollar amounts (allow optional decimal)
    /\$[\d,]+(?:\.\d+)?/g,
    // Numeric counts with common units
    /\b\d+(?:,\d{3})*(?:\.\d+)?\s*(?:agents?|advisors?|years?|states?|days?|hours?|percent|%)\b/gi,
    // Bare 4-digit years
    /\b(?:19|20)\d{2}\b/g,
    // Brand + industry-term phrases
    /\b(?:LuxRally Travel|LuxRally|Seller of Travel(?:\s+(?:license|registration|program))?|host agency|host agencies|host model)\b/gi,
    // Friction-killer / value-prop phrases
    /\b(?:free training|no fees?|no commitment|80\/20|80\/20 split|in 30 days)\b/gi,
];

interface Range {
    start: number;
    end: number;
}

function findRanges(text: string): Range[] {
    const ranges: Range[] = [];
    for (const pattern of PATTERNS) {
        // Reset lastIndex on the global regex
        pattern.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = pattern.exec(text)) !== null) {
            ranges.push({ start: match.index, end: match.index + match[0].length });
            // Guard against zero-width matches
            if (match[0].length === 0) pattern.lastIndex++;
        }
    }
    if (ranges.length === 0) return ranges;
    ranges.sort((a, b) => a.start - b.start || b.end - a.end);
    const merged: Range[] = [];
    for (const r of ranges) {
        const last = merged[merged.length - 1];
        if (!last || r.start > last.end) {
            merged.push({ start: r.start, end: r.end });
        } else {
            last.end = Math.max(last.end, r.end);
        }
    }
    return merged;
}

export function highlightFacts(text: string | null | undefined): ReactNode {
    if (!text) return null;
    const ranges = findRanges(text);
    if (ranges.length === 0) return text;
    const out: ReactNode[] = [];
    let cursor = 0;
    for (const r of ranges) {
        if (r.start > cursor) out.push(text.slice(cursor, r.start));
        out.push(
            <strong key={`${r.start}-${r.end}`} className="font-semibold text-[var(--cms-ink)]">
                {text.slice(r.start, r.end)}
            </strong>,
        );
        cursor = r.end;
    }
    if (cursor < text.length) out.push(text.slice(cursor));
    return <>{out}</>;
}
